"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useFiltersStore } from "@/stores/filtersStore";

interface UseImageUploadOptions {
  /** Callback fired when image search is triggered */
  onSearchComplete?: () => void;
  /** Callback fired when image mode is closed */
  onClose?: () => void;
}

/**
 * Hook for managing image upload for visual search functionality.
 *
 * Handles drag-and-drop, file selection, paste events, and URL input.
 * Automatically triggers image search when an image is loaded.
 *
 * @param options - Configuration options
 * @returns Object containing:
 * - State: imageMode, previewUrl, pasteLink, isDragging
 * - Setters: setPasteLink
 * - Refs: fileInputRef (for hidden file input)
 * - Handlers: handleFileChange, handleDragEnter/Leave/Over/Drop,
 *   handleLinkSearch, openImageMode, closeImageMode, triggerFileInput
 *
 * @example
 * ```tsx
 * function ImageSearchButton() {
 *   const {
 *     imageMode,
 *     previewUrl,
 *     openImageMode,
 *     closeImageMode,
 *     fileInputRef,
 *     triggerFileInput,
 *     isDragging,
 *     handleDragEnter,
 *     handleDragLeave,
 *     handleDrop,
 *   } = useImageUpload({
 *     onSearchComplete: () => console.log("Search started"),
 *   });
 *
 *   return (
 *     <>
 *       <button onClick={openImageMode}>Search by Image</button>
 *       {imageMode && (
 *         <Modal onClose={closeImageMode}>
 *           <Dropzone
 *             isDragging={isDragging}
 *             onDragEnter={handleDragEnter}
 *             onDragLeave={handleDragLeave}
 *             onDrop={handleDrop}
 *           >
 *             {previewUrl && <img src={previewUrl} alt="Preview" />}
 *           </Dropzone>
 *           <input
 *             ref={fileInputRef}
 *             type="file"
 *             hidden
 *             onChange={(e) => handleFileChange(e.target.files?.[0])}
 *           />
 *           <button onClick={triggerFileInput}>Upload</button>
 *         </Modal>
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function useImageUpload(options: UseImageUploadOptions = {}) {
  const { onSearchComplete, onClose } = options;

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const { startSearch, exit: exitImageSearch } = useImageSearchStore((state) => state.actions);
  const sourceItem = useImageSearchStore((state) => state.sourceItem);

  const [imageMode, setImageMode] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  // Keeps the original un-cropped image so re-crop can start from scratch
  const [originalImageUrl, setOriginalImageUrl] = useState("");

  // Use the store's sourceItem imageurl if available, otherwise use local state
  const previewUrl = sourceItem?.imageurl || localPreviewUrl;
  const [pasteLink, setPasteLink] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle paste for image upload when in image mode
  useEffect(() => {
    if (!imageMode) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result as string;
              setOriginalImageUrl(base64);
              setLocalPreviewUrl(base64);
            };
            reader.readAsDataURL(file);
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [imageMode]);

  const handleFileChange = useCallback((file: File | null) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return; // 10MB limit
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setOriginalImageUrl(base64);
      setLocalPreviewUrl(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const file = e.dataTransfer.files?.[0];
      if (file?.type.startsWith("image/")) {
        handleFileChange(file);
      }
    },
    [handleFileChange]
  );

  const handleImageSearch = useCallback(() => {
    if (!previewUrl) return;

    const fakeItem = { imageurl: previewUrl } as SearchResultItem;
    startSearch(fakeItem, activeFilters);
    onSearchComplete?.();
  }, [previewUrl, startSearch, activeFilters, onSearchComplete]);

  const handleLinkSearch = useCallback(() => {
    const link = pasteLink.trim();
    if (!link) return;

    const fakeItem = { imageurl: link } as SearchResultItem;
    startSearch(fakeItem, activeFilters);
    onSearchComplete?.();
  }, [pasteLink, startSearch, activeFilters, onSearchComplete]);

  const openImageMode = useCallback(() => {
    setImageMode(true);
  }, []);

  const closeImageMode = useCallback(() => {
    setImageMode(false);
    setLocalPreviewUrl("");
    setOriginalImageUrl("");
    setPasteLink("");
    setIsDragging(false);
    dragCounter.current = 0;
    exitImageSearch();
    onClose?.();
  }, [exitImageSearch, onClose]);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    // State
    imageMode,
    previewUrl,
    originalImageUrl,
    pasteLink,
    isDragging,

    // Setters
    setPasteLink,

    // Refs
    fileInputRef,

    // Handlers
    handleFileChange,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleImageSearch,
    handleLinkSearch,
    openImageMode,
    closeImageMode,
    triggerFileInput,
  };
}
